const DEFAULT_DOMAIN_SUFFIX = 'mykio.store';

const KIO_PLATFORM_SUFFIXES = ['mykio.site', 'mykio.store', 'mykio.shop', 'mykio.biz'];

/** IDs renvoyés par l'API (souvent sans domain_name) — aligné avec l'onboarding. */
export const KIO_PLATFORM_SUB_DOMAINS = [
  { id: 5, domain_name: 'mykio.shop' },
  { id: 4, domain_name: 'mykio.store' },
  { id: 2, domain_name: 'mykio.biz' },
  { id: 3, domain_name: 'mykio.site' },
];

const KIO_SUB_DOMAIN_BY_ID = Object.fromEntries(
  KIO_PLATFORM_SUB_DOMAINS.map(({ id, domain_name }) => [Number(id), domain_name]),
);

function subDomainNameFromId(id, explicitName) {
  return normalizeHost(explicitName) || KIO_SUB_DOMAIN_BY_ID[Number(id)] || '';
}

/** Hôtes techniques / dev — pas une boutique cliente connectée. */
const NON_STOREFRONT_HOST_PATTERNS = [
  /\.myftp\./i,
  /\.github\.io$/i,
  /\.netlify\.app$/i,
  /\.vercel\.app$/i,
];

export function normalizeHost(value) {
  return String(value || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function pickStoreCustomDomain(value) {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) {
    if (!value.length) return '';
    const first = value.find(Boolean);
    return first ? pickStoreCustomDomain(first) : '';
  }

  let str = String(value).trim();
  if (!str || str === '[]') return '';

  for (let i = 0; i < 5; i += 1) {
    const arrayLike = str.match(/^\[\s*['"]?([^'"\[\]]+)['"]?\s*\]$/);
    if (!arrayLike) break;
    str = arrayLike[1].trim();
  }

  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str.replace(/'/g, '"'));
      if (Array.isArray(parsed)) return pickStoreCustomDomain(parsed);
    } catch { /* ignore */ }
  }

  return str.replace(/^['"]+|['"]+$/g, '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function mapSubDomain(source) {
  const raw = source?.sub_domain ?? source?.subDomain;
  if (raw && typeof raw === 'object') {
    const id = raw.id;
    return {
      id,
      domain_name: subDomainNameFromId(id, raw.domain_name ?? raw.domainName),
    };
  }
  if (raw != null && raw !== '') {
    return { id: raw, domain_name: subDomainNameFromId(raw) };
  }
  return null;
}

function pickStorefrontHostFromUrl(value) {
  const host = normalizeHost(value);
  if (!host || host.includes('admin.')) return '';
  if (isKioPlatformHost(host) || isLikelyStorefrontCustomHost(host)) return host;
  return '';
}

export function isKioPlatformHost(host) {
  const h = normalizeHost(host);
  if (!h) return false;
  return KIO_PLATFORM_SUFFIXES.some((suffix) => h === suffix || h.endsWith(`.${suffix}`));
}

export function isLikelyStorefrontCustomHost(host) {
  const h = normalizeHost(host);
  if (!h || isKioPlatformHost(h)) return false;
  return !NON_STOREFRONT_HOST_PATTERNS.some((re) => re.test(h));
}

export function resolveStoreDefaultHost(store) {
  const slug = normalizeHost(store?.slug);
  if (!slug) return '';

  const subDomain = store?.subDomain ?? mapSubDomain(store);
  const extension = normalizeHost(subDomain?.domain_name) || DEFAULT_DOMAIN_SUFFIX;
  return `${slug}.${extension}`;
}

/** Domaine custom actif — lit custom_domain (API) puis customDomain (cache local). */
export function resolveStoreCustomDomain(store) {
  return pickStoreCustomDomain(store?.custom_domain)
    || normalizeHost(store?.customDomain)
    || '';
}

export function pickStoreCustomHost(store) {
  const customHost = resolveStoreCustomDomain(store);
  return isLikelyStorefrontCustomHost(customHost) ? customHost : '';
}

export function resolveStoreDomain(store) {
  if (!store?.slug) return { domain: '', isCustom: false };

  const defaultHost = resolveStoreDefaultHost(store);
  const customHost = pickStoreCustomHost(store);
  if (customHost) {
    return { domain: customHost, isCustom: true };
  }

  return {
    domain: defaultHost,
    isCustom: false,
  };
}

/** URL publique boutique : domaine custom connecté, sinon {slug}.{extension} */
export function resolveStorefrontHost(store) {
  const customHost = pickStoreCustomHost(store);
  if (customHost) return customHost;

  // Recalculer depuis slug + sub_domain (évite un defaultDomain localStorage obsolète)
  const computedDefault = resolveStoreDefaultHost(store);
  if (computedDefault) return computedDefault;

  return pickStorefrontHostFromUrl(store?.url)
    || normalizeHost(store?.defaultDomain)
    || normalizeHost(store?.domain)
    || '';
}

/**
 * Origine des URLs boutique (vitrine, produit, customizer, funnels).
 * Dev : REACT_APP_STOREFRONT_ORIGIN (ex. http://localhost:5000) remplace https://{host}.
 */
export const STOREFRONT_ORIGIN_OVERRIDE = String(process.env.REACT_APP_STOREFRONT_ORIGIN || '').trim().replace(/\/+$/, '');

export function storefrontOrigin(host) {
  if (STOREFRONT_ORIGIN_OVERRIDE) return STOREFRONT_ORIGIN_OVERRIDE;
  return host ? `https://${host}` : '';
}

export function buildStorefrontUrl(store) {
  const domain = resolveStorefrontHost(store);
  if (!domain) return '';
  return `${storefrontOrigin(domain)}/`;
}

export function buildProductStorefrontUrl(store, productSlug) {
  const domain = resolveStorefrontHost(store);
  const slug = String(productSlug ?? '').trim().replace(/^\/+|\/+$/g, '');
  if (!domain || !slug) return '';
  return `${storefrontOrigin(domain)}/products/${slug}/`;
}
